/* Leading-zero guard for every <input type="number"> in the app, installed
 * once (see index.js) instead of patched into hundreds of fields.
 *
 * The symptom: a numeric box seeded with 0 shows "022" after typing 22 —
 * and KEEPS showing it, because React deliberately leaves a focused number
 * input's text alone when it is numerically equal to the value it would
 * render ("022" == 22), so the controlled re-render never cleans it up.
 *
 * The fix: a document-level CAPTURE listener on the native `input` event.
 * Capture fires before React's own root-level listener reads the value, so
 * stripping the leading zeros here means the handler each field already has
 * receives the corrected text, state and screen agree, and no field needs
 * to know any of this happened. Decimals survive ("0.5" has no zero to
 * strip; "00.5" becomes "0.5"), signs survive ("-022" → "-22"), and a lone
 * "0" is a real answer left exactly as typed.
 */
export function installNumberInputGuard() {
  /* react-dom replaces each input's `value` setter with a tracked one that
     records the last value it saw and drops events whose value "didn't
     change". A plain `el.value = fixed` would update that tracker and make
     React swallow this very keystroke — so the write goes through the NATIVE
     prototype setter, leaving the tracker stale and the change visible. */
  const nativeValueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype, 'value',
  ).set;
  document.addEventListener(
    'input',
    (e) => {
      const el = e.target;
      if (!el || el.tagName !== 'INPUT' || el.type !== 'number') return;
      const v = el.value;
      if (!/^[+-]?0\d/.test(v)) return;
      nativeValueSetter.call(el, v.replace(/^([+-]?)0+(?=\d)/, '$1'));
    },
    true,
  );
}
